import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader, { MobileSearchBar } from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import Breadcrumb from "@/components/base/Breadcrumb";
import ResourceCard from "@/components/base/ResourceCard";
import ShareModal from "@/components/base/ShareModal";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getCategoryMeta, getRelatedResources, getResourceById } from "@/utils/content";

const detailRows = [
  { label: "类型", field: "type" },
  { label: "地区", field: "region" },
  { label: "年份", field: "year" },
  { label: "时长", field: "duration" },
  { label: "清晰度", field: "quality" },
  { label: "发布时间", field: "publishDate" },
] as const;

export default function DetailPage() {
  const { id = "" } = useParams();
  const resource = getResourceById(id);
  const [shareOpen, setShareOpen] = useState(false);

  const categoryMeta = resource ? getCategoryMeta(resource.category) : undefined;

  useDocumentMeta(
    resource ? `${resource.title} - 无码高清资源详情` : "资源不存在 - 无码高清资源平台",
    resource
      ? `了解${resource.title}的详细信息、高清封面及相关内容推荐。`
      : "未找到对应的资源内容，请返回首页或重新搜索。",
    resource ? `${resource.title},${resource.tags.join(",")},高清资源` : undefined,
  );

  if (!resource) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-20">
          <div className="max-w-md w-full text-center bg-background-50 border border-background-200 rounded-lg p-8">
            <span className="mx-auto w-14 h-14 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
              <i className="ri-file-unknow-line text-2xl" aria-hidden="true"></i>
            </span>
            <h1 className="mt-4 font-heading font-extrabold text-xl text-foreground-950">没有找到这条资源</h1>
            <p className="mt-2 text-sm text-foreground-600 leading-6">
              资源编号可能已经变更，或者链接输入有误。你可以返回首页浏览最新内容，或者直接搜索关键词。
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              <Link
                to="/"
                className="h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center cursor-pointer transition-colors whitespace-nowrap"
              >
                返回首页
              </Link>
              <Link
                to="/search"
                className="h-10 px-5 rounded-md border border-background-300 text-foreground-700 text-sm flex items-center cursor-pointer hover:border-primary-300 transition-colors whitespace-nowrap"
              >
                去搜索
              </Link>
            </div>
          </div>
        </main>
        <MobileSearchBar />
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedResources(resource, 4);
  const shareUrl = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 pb-24 md:pb-0">
        <div className="wrap pt-5 md:pt-7">
          <Breadcrumb
            items={[
              { label: "分类" },
              { label: `${categoryMeta?.label ?? "资源"}资源`, to: `/category/${resource.category}` },
              { label: resource.title },
            ]}
          />

          <div className="mt-4 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5 md:gap-8 items-start">
            <aside className="lg:sticky lg:top-24">
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <div className="relative w-full aspect-[3/4] bg-background-200">
                  <img
                    src={resource.cover}
                    alt={`${resource.title} 高清主封面`}
                    title={`${resource.title} 高清无码资源封面`}
                    className="w-full h-full object-cover object-top"
                  />
                  <span className="absolute top-3 left-3 chip bg-primary-500 text-background-50">{resource.quality}</span>
                  <span className="absolute top-3 right-3 chip bg-accent-500 text-background-50">
                    <i className="ri-star-fill" aria-hidden="true"></i>
                    {resource.rating.toFixed(1)}
                  </span>
                </div>
                <div className="p-4 md:p-5">
                  <h2 className="text-xs font-semibold text-foreground-500 tracking-wide mb-3">资源资料</h2>
                  <dl className="space-y-2.5 text-sm">
                    {detailRows.map((row) => (
                      <div key={row.field} className="flex items-start justify-between gap-4">
                        <dt className="text-foreground-500 shrink-0">{row.label}</dt>
                        <dd className="text-foreground-800 text-right">{resource[row.field]}</dd>
                      </div>
                    ))}
                    <div className="flex items-start justify-between gap-4">
                      <dt className="text-foreground-500 shrink-0">累计浏览</dt>
                      <dd className="text-foreground-800 text-right">{resource.views.toLocaleString()} 次</dd>
                    </div>
                  </dl>

                  <div className="mt-4 pt-4 border-t border-background-200">
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      className="w-full h-10 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center justify-center gap-2 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <i className="ri-share-forward-line" aria-hidden="true"></i>
                      分享这个资源
                    </button>
                    <p className="mt-2 text-[11px] text-foreground-400 text-center leading-5">
                      本站为资讯展示站点，不提供下载与播放
                    </p>
                  </div>
                </div>
              </div>
            </aside>

            <article className="min-w-0">
              <div className="bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="chip bg-primary-50 text-primary-700">{categoryMeta?.label}</span>
                  <span className="chip bg-secondary-100 text-secondary-900">{resource.type}</span>
                  <span className="chip bg-background-100 text-foreground-600">{resource.region}</span>
                  <span className="chip bg-background-100 text-foreground-600">{resource.year}</span>
                </div>

                <h1 className="mt-3 font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 leading-tight">
                  {resource.title}
                </h1>

                <div className="mt-3 flex items-center gap-4 text-xs md:text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1 text-accent-600 font-semibold">
                    <i className="ri-star-fill" aria-hidden="true"></i>
                    {resource.rating.toFixed(1)} 分
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {resource.views.toLocaleString()} 次浏览
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line" aria-hidden="true"></i>
                    {resource.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" aria-hidden="true"></i>
                    {resource.duration}
                  </span>
                </div>

                <p className="mt-5 text-sm md:text-base text-foreground-700 leading-8 bg-background-100 border-l-2 border-accent-500 rounded-r-md px-4 py-3">
                  {resource.summary}
                </p>

                <section className="mt-6">
                  <h2 className="font-heading font-bold text-base md:text-lg text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full bg-primary-500"></span>
                    剧情介绍与资源说明
                  </h2>
                  <p className="mt-3 text-sm md:text-base text-foreground-600 leading-8">{resource.description}</p>
                  <p className="mt-3 text-sm text-foreground-500 leading-7">
                    资源版本：{resource.quality} · {resource.duration} · 更新于 {resource.publishDate}。以上内容为虚构演示资料，用于展示详情页的信息结构。
                  </p>
                </section>

                <section className="mt-6">
                  <h2 className="font-heading font-bold text-base md:text-lg text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full bg-primary-500"></span>
                    相关标签
                  </h2>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {resource.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/category/${resource.category}?tag=${encodeURIComponent(tag)}`}
                        className="chip bg-accent-50 text-accent-800 border border-accent-200 cursor-pointer hover:bg-accent-100 transition-colors"
                      >
                        <i className="ri-price-tag-3-line" aria-hidden="true"></i>
                        {tag}
                      </Link>
                    ))}
                    <Link
                      to={`/category/${resource.category}`}
                      className="chip bg-background-100 text-foreground-600 border border-background-200 cursor-pointer hover:bg-background-200 transition-colors"
                    >
                      查看全部{categoryMeta?.label}
                    </Link>
                  </div>
                </section>

                <section className="mt-6 pt-5 border-t border-background-200">
                  <h2 className="font-heading font-bold text-base md:text-lg text-foreground-950 flex items-center gap-2">
                    <span className="w-1 h-4 rounded-full bg-primary-500"></span>
                    分享给朋友
                  </h2>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      className="h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center gap-2 cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-link" aria-hidden="true"></i>
                      复制链接
                    </button>
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      className="h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center gap-2 cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-wechat-line" aria-hidden="true"></i>
                      微信
                    </button>
                    <button
                      type="button"
                      onClick={() => setShareOpen(true)}
                      className="h-9 px-4 rounded-md border border-background-300 text-sm text-foreground-700 flex items-center gap-2 cursor-pointer hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-weibo-line" aria-hidden="true"></i>
                      微博
                    </button>
                    <span className="text-[11px] text-foreground-400">分享功能为演示效果</span>
                  </div>
                </section>
              </div>
            </article>
          </div>

          <section aria-label="相关内容推荐" className="mt-10 md:mt-14">
            <div className="flex items-end justify-between gap-4 mb-5">
              <div>
                <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
                  <span className="w-8 h-8 rounded-md bg-accent-100 text-accent-700 flex items-center justify-center">
                    <i className="ri-magic-line text-base" aria-hidden="true"></i>
                  </span>
                  相关内容推荐
                </h2>
                <p className="mt-2 text-xs md:text-sm text-foreground-500">
                  基于标签关联的虚拟推荐结果，仅用于演示推荐模块
                </p>
              </div>
              <Link
                to={`/category/${resource.category}`}
                className="text-xs md:text-sm text-foreground-500 hover:text-primary-600 flex items-center gap-1 cursor-pointer whitespace-nowrap transition-colors"
              >
                更多{categoryMeta?.label}资源
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
              {related.map((item) => (
                <ResourceCard key={item.id} resource={item} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <ShareModal
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        title={resource.title}
        url={shareUrl}
      />
      <MobileSearchBar />
      <SiteFooter />
    </div>
  );
}